import { useState } from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, styled } from '@mui/material';

import { SubmitButton } from '../buttons';

type DeactivateUserDialogProps = {
  trigger: (handleOpen: () => void) => JSX.Element;
  onDeactivate: (hideArticles: boolean) => Promise<unknown>;
  disabled?: boolean;
  loading?: boolean;
};

const StyledDialogActions = styled(DialogActions)({
  padding: '1rem',
  flexWrap: 'wrap',
  gap: '8px',
});

/**
 * Lets an administrator choose what happens to a user's existing articles
 * while deactivating the account. Both choices preserve the account data.
 */
export const DeactivateUserDialog = ({ trigger, onDeactivate, disabled = false, loading = false }: DeactivateUserDialogProps) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const isBusy = disabled || loading || submitting;

  const handleDeactivate = async (hideArticles: boolean) => {
    setSubmitting(true);
    try {
      await onDeactivate(hideArticles);
      setOpen(false);
    } catch {
      // The calling mutation exposes the error to the page-level feedback UI.
      // Keep this dialog open so the administrator can retry or cancel.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {trigger(() => setOpen(true))}
      <Dialog open={open} onClose={() => !isBusy && setOpen(false)} aria-labelledby="deactivate-user-dialog-title">
        <DialogTitle id="deactivate-user-dialog-title">Deactivate User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Choose how to handle this user&apos;s articles. Keeping them visible shows the author as “Deleted User”; hiding them preserves
            the data so it can be restored when the account is reinstated.
          </DialogContentText>
        </DialogContent>
        <StyledDialogActions>
          <Button disabled={isBusy} onClick={() => setOpen(false)} color="secondary">
            Cancel
          </Button>
          <SubmitButton disabled={isBusy} loading={submitting} variant="outlined" type="button" onClick={() => handleDeactivate(false)}>
            Deactivate &amp; Keep Articles
          </SubmitButton>
          <SubmitButton disabled={isBusy} loading={submitting} variant="contained" type="button" onClick={() => handleDeactivate(true)}>
            Deactivate &amp; Hide Articles
          </SubmitButton>
        </StyledDialogActions>
      </Dialog>
    </>
  );
};
